import { usePopover } from 'minimal-shared/hooks';
import { useState } from 'react';
import { Box, Grid, Badge, Button, MenuList, MenuItem, IconButton, Typography, LinearProgress } from '@mui/material';
import { Chip } from '@mui/material';
import Iconify from '../../iconify';
import { CustomPopover } from '../../custom-popover';
import { amber, green, purple, red, blue } from '@mui/material/colors';
import { FiberManualRecord } from '@mui/icons-material';
import { BackArrowButton } from '../Components';

// ----------------------------------------------------------------------

const VIEW_OPTIONS = [
  { value: 'dayGridMonth', label: 'Month', icon: 'mingcute:calendar-month-line' },
  //{ value: 'timeGridWeek', label: 'Week', icon: 'mingcute:calendar-week-line' },
  //{ value: 'timeGridDay', label: 'Day', icon: 'mingcute:calendar-day-line' },
  { value: 'listWeek', label: 'Agenda', icon: 'fluent:calendar-agenda-24-regular' },
];

const TYPES = [
  {
    value: 'form_entry',
    text: 'Form',
    color: green[500],
  },
  {
    value: 'workflow',
    text: 'Task',
    color: purple[500],
  },
  {
    value: 'process_step',
    text: 'Process',
    color: amber[500],
  },
  {
    value: 'asset',
    text: 'Asset',
    color: blue[500],
  },
  {
    value: 'routine',
    text: 'Routine',
    color: blue[900],
  },
  {
    value: 'audit',
    text: 'Audit',
    color: blue[500],
  },
];

// ----------------------------------------------------------------------

export function CalendarToolbar({
  date,
  view,
  loading,
  onToday,
  canReset,
  onNextDate,
  onPrevDate,
  onChangeView,
  onOpenFilters,
  onCompletedChange,
  onTypeFilterChange,
  withCompleted,
}) {
  const [typeFilter, setTypeFilter] = useState([]);
  const menuActions = usePopover();

  const selectedItem = VIEW_OPTIONS.filter((item) => item.value === view)[0];

  const renderMenuActions = () => (
    <CustomPopover
      open={menuActions.open}
      anchorEl={menuActions.anchorEl}
      onClose={menuActions.onClose}
      slotProps={{ arrow: { placement: 'top-left' } }}
    >
      <MenuList>
        {VIEW_OPTIONS.map((viewOption) => (
          <MenuItem
            key={viewOption.value}
            selected={viewOption.value === view}
            onClick={() => {
              menuActions.onClose();
              onChangeView(viewOption.value);
            }}
          >
            <Iconify icon={viewOption.icon} />
            {viewOption.label}
          </MenuItem>
        ))}
      </MenuList>
    </CustomPopover>
  );

  return (
    <>
      <Grid
        container
        sx={{
          p: 2,
          position: 'relative',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Grid container item lg={5} xs={12} alignItems={'center'} gap={1}>
          <BackArrowButton />

          {TYPES.map((item) => (
            <Chip
              size="small"
              key={item.value}
              label={item.text}
              variant={typeFilter.includes(item.value) ? 'filled' : 'outlined'}
              icon={<FiberManualRecord />}
              sx={
                typeFilter.includes(item.value)
                  ? { background: item.color, color: (theme) => theme.palette.getContrastText(item.color) }
                  : { color: item.color, borderColor: item.color }
              }
              onClick={() => {
                const result = typeFilter.includes(item.value)
                  ? typeFilter.filter((x) => x !== item.value)
                  : [...typeFilter, item.value];
                setTypeFilter(result);
                onTypeFilterChange(result);
              }}
            />
          ))}
        </Grid>

        <Grid
          container
          item
          lg={2}
          md={6}
          xs={12}
          alignItems={'center'}
          justifyContent={'center'}
          gap={1}
          wrap="nowrap"
        >
          <Chip label="beta" size="small" sx={{ background: blue[500], color: 'white' }} />
          <IconButton onClick={onPrevDate}>
            <Iconify icon="eva:arrow-ios-back-fill" />
          </IconButton>

          <Typography variant="h6">{date}</Typography>

          <IconButton onClick={onNextDate}>
            <Iconify icon="eva:arrow-ios-forward-fill" />
          </IconButton>
        </Grid>

        <Grid container item lg={5} md={6} xs={12} alignItems={'center'} gap={1} justifyContent="flex-end">
          <Button size="small" color="primary" onClick={onToday}>
            Today
          </Button>
          <Button size="small" color="primary" onClick={onCompletedChange}>
            {`${!withCompleted ? 'With' : 'Without'} completed`}
          </Button>

          {false && (
            <IconButton onClick={onOpenFilters}>
              <Badge color="error" variant="dot" invisible={!canReset}>
                <Iconify icon="ic:round-filter-list" />
              </Badge>
            </IconButton>
          )}

          <Button
            size="small"
            variant="contained"
            onClick={menuActions.onOpen}
            startIcon={<Iconify icon={selectedItem.icon} />}
            endIcon={<Iconify icon="eva:arrow-ios-downward-fill" sx={{ ml: -0.5 }} />}
            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
          >
            {selectedItem.label}
          </Button>
        </Grid>

        {loading && (
          <LinearProgress
            color="primary"
            sx={{
              left: 0,
              width: 1,
              height: 2,
              bottom: 0,
              borderRadius: 0,
              position: 'absolute',
            }}
          />
        )}
      </Grid>

      {renderMenuActions()}
    </>
  );
}
